import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Faq from "@/pages/about/components/Faq";

const principles = [
  {
    icon: "ri-verified-badge-line",
    title: "先核实，再发布",
    text: "每条爆料至少经过一轮来源核对，时间线和关键细节交叉比对后才进入编辑池。",
  },
  {
    icon: "ri-emotion-normal-line",
    title: "不预设立场",
    text: "尽量呈现各方说法，避免用带节奏的表达引导读者情绪，让阅读者自己形成判断。",
  },
  {
    icon: "ri-user-heart-line",
    title: "尊重当事人",
    text: "涉及未成年人、普通网友隐私的内容一律不做深挖，娱乐归娱乐，边界要守住。",
  },
  {
    icon: "ri-refresh-line",
    title: "有错就改",
    text: "收到有效指正后，编辑会在 24 小时内完成更正，并在文末说明修改内容与时间。",
  },
];

const values = [
  { num: "08:00", label: "每日更新完成时间" },
  { num: "5", label: "持续更新栏目" },
  { num: "24h", label: "纠错响应时效" },
  { num: "100%", label: "人工编辑把关" },
];

const voices = [
  {
    name: "追剧的小林",
    role: "老用户 · 追了 8 个月",
    text: "最舒服的是它不会硬塞结论，时间线整理得非常清楚，看完自己能判断个大概。",
  },
  {
    name: "阿哲不加糖",
    role: "内容从业者",
    text: "做选题的时候常来这里看热榜，分类清楚，适合快速了解一个话题的传播脉络。",
  },
  {
    name: "深夜吃瓜组",
    role: "老用户 · 追了 1 年",
    text: "更新稳定，早上通勤刷一遍就够看当天的重点了，不用在各个平台之间来回跳。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们 | 爆瓜爆料社",
    description:
      "了解爆瓜爆料社：一个坚持人工编辑把关的热门爆料聚合门户，介绍网站定位、发展理念、内容原则、用户口碑与联系方式。",
    keywords: "爆瓜爆料,关于我们,内容原则,爆料网站,编辑整理",
    canonical: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们",
        url: "https://app-test.cn/about",
        description: "爆瓜爆料社的品牌介绍、内容原则与联系方式。",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "爆瓜爆料社的内容是从哪里来的？",
            acceptedAnswer: {
              "@type": "Answer",
              text: "内容主要来自公开报道、平台公开动态与热心网友的投稿线索。编辑会对线索做基础核对，涉及当事人未证实的部分，我们会在正文中明确标注为传闻或未经证实。",
            },
          },
          {
            "@type": "Question",
            name: "可以匿名投稿吗？",
            acceptedAnswer: {
              "@type": "Answer",
              text: "可以。投稿时不需要填写真实姓名，我们在核实过程中也会对来源信息严格保密。",
            },
          },
          {
            "@type": "Question",
            name: "发现内容有误怎么反馈？",
            acceptedAnswer: {
              "@type": "Answer",
              text: "通过联系页面的表单提交指正说明，附上相关链接或截图。核实后我们会在 24 小时内更正或下架相关内容。",
            },
          },
        ],
      },
    ],
  });

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            我们只做一件事：把瓜理清楚
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-600 md:text-base">
            爆瓜爆料社是一个专注热门爆料整理的内容聚合门户。我们不做耸动的标题党，也不替读者下结论，
            而是把散落在各个角落的信息，按时间线和分类整理好，让你花最少的时间看懂一件事的来龙去脉。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1240px] px-4 py-12 md:px-6">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
          <div className="h-[260px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[360px]">
            <img
              src="https://readdy.ai/api/search-image?query=cinematic%20warm-toned%20abstract%20image%20of%20a%20quiet%20editorial%20desk%20with%20warm%20amber%20lamp%20light%2C%20open%20notebook%20and%20coffee%2C%20soft%20film%20grain%2C%20editorial%20mood%2C%20no%20visible%20faces&width=1000&height=800&seq=about-01&orientation=landscape"
              alt="编辑整理爆料线索的工作场景"
              title="爆瓜爆料社 编辑工作场景"
              className="h-full w-full object-cover"
            />
          </div>
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
              <i className="ri-building-4-line" aria-hidden="true" />
              品牌介绍
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950">
              一个由编辑主导、而非算法主导的爆料门户
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              很多内容平台靠推荐算法决定你看到什么，刷得越多越容易陷入信息茧房。爆瓜爆料社选择另一条路：
              每条进入首页的内容，都经过编辑人工筛选与顺序编排，确保你在最短时间内看到当天真正值得关注的几个话题。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              我们的编辑团队覆盖娱乐、社会与影视三个方向，日常工作是盯线索、核时间线、写摘要，
              以及把复杂的事件拆成普通人能看懂的段落。
            </p>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {values.map((v) => (
                <div key={v.label} className="rounded-lg bg-background-100/70 p-3 text-center">
                  <p className="font-heading text-xl font-bold text-primary-600">{v.num}</p>
                  <p className="mt-1 text-[11px] leading-4 text-foreground-500">{v.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100/60">
        <div className="mx-auto max-w-[1240px] px-4 py-12 md:px-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-6 md:p-8">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className="ri-focus-3-line text-xl" aria-hidden="true" />
              </span>
              <h2 className="mt-4 font-heading text-xl font-bold text-foreground-950">网站定位</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                面向关注娱乐与社会热点的泛内容读者，提供一个信息密度高、结构清晰、不制造焦虑的吃瓜入口。
                我们相信读者有判断力，缺的只是被整理好的信息。
              </p>
            </div>
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-6 md:p-8">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-lightbulb-flash-line text-xl" aria-hidden="true" />
              </span>
              <h2 className="mt-4 font-heading text-xl font-bold text-foreground-950">发展理念</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                把热闹还给娱乐，把克制留给编辑。我们不追求最快的首发，但希望成为那个被反复打开的整理者，
                长期提供稳定、可回溯、可验证的内容价值。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="principles" className="mx-auto max-w-[1240px] scroll-mt-28 px-4 py-12 md:px-6">
        <h2 className="font-heading text-2xl font-bold text-foreground-950">内容原则</h2>
        <p className="mt-2 text-sm text-foreground-500">这四条，是我们判断一条内容能不能发的底线。</p>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {principles.map((p, idx) => (
            <div
              key={p.title}
              className="flex flex-col rounded-lg border border-background-200/80 bg-background-50 p-5"
            >
              <span
                className={`flex h-10 w-10 items-center justify-center rounded-md ${
                  idx % 2 === 0 ? "bg-primary-100 text-primary-600" : "bg-accent-100 text-accent-700"
                }`}
              >
                <i className={`${p.icon} text-lg`} aria-hidden="true" />
              </span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-900">{p.title}</h3>
              <p className="mt-2 text-sm leading-6 text-foreground-500">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full bg-background-100/60">
        <div className="mx-auto max-w-[1240px] px-4 py-12 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">用户口碑</h2>
          <p className="mt-2 text-sm text-foreground-500">来自读者的真实反馈（已获授权匿名展示）。</p>
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
            {voices.map((v) => (
              <figure
                key={v.name}
                className="flex flex-col rounded-lg border border-background-200/80 bg-background-50 p-6"
              >
                <i className="ri-double-quotes-l text-2xl text-accent-400" aria-hidden="true" />
                <blockquote className="mt-3 flex-1 text-sm leading-7 text-foreground-700">
                  {v.text}
                </blockquote>
                <figcaption className="mt-4 border-t border-background-100 pt-4">
                  <p className="font-heading text-sm font-bold text-foreground-900">{v.name}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{v.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1240px] px-4 py-12 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">常见问题</h2>
            <p className="mt-2 text-sm leading-6 text-foreground-500">
              关于内容来源、投稿方式与纠错流程，我们把最常被问到的问题整理在这里。
            </p>
            <Link
              to="/contact"
              className="mt-5 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-chat-smile-2-line" aria-hidden="true" />
              还有疑问？联系我们
            </Link>
          </div>
          <div className="lg:col-span-2">
            <Faq />
          </div>
        </div>
      </section>
    </>
  );
}